/**
 * useLoginPrompts — orchestrates WelcomeModal and ProfileCompletionModal.
 *
 * Logic (ADR-4):
 *   1. Wait for loading === false (profile fetch resolved).
 *   2. If profile === null → showWelcome = true (first login, no profile document).
 *   3. If profile exists + isProfileComplete().complete === false
 *      + sessionStorage "autodieta:completionPromptSeen" NOT set → showCompletion = true.
 *   4. If profile is complete → nothing shown.
 *
 * sessionStorage (not localStorage) — shown once per tab session; clears on
 * browser close so next login shows the reminder again (ADR-4 decision).
 *
 * Session keys:
 *   "autodieta:welcomeSeen"         — dismissal of WelcomeModal
 *   "autodieta:completionPromptSeen" — dismissal of ProfileCompletionModal
 */

import { useState, useEffect } from "react";
import type { HealthProfile } from "@autodieta/shared";
import { isProfileComplete } from "../data/profileCompleteness.js";

// ---------------------------------------------------------------------------
// Session storage keys (namespaced to avoid collisions)
// ---------------------------------------------------------------------------

export const KEY_WELCOME = "autodieta:welcomeSeen";
export const KEY_COMPLETION = "autodieta:completionPromptSeen";

// ---------------------------------------------------------------------------
// Pure decision function — exported for unit testing without hooks
// ---------------------------------------------------------------------------

export interface LoginPromptsDecision {
  showWelcome: boolean;
  showCompletion: boolean;
  missing: string[];
}

/**
 * Compute which login prompt (if any) should be shown.
 *
 * Pure function: no hooks, no side-effects.
 * The `welcomeSeen` / `completionSeen` flags are passed as parameters
 * so tests can verify logic without sessionStorage or React hooks.
 */
export function computeLoginPromptsState(
  profile: Partial<HealthProfile> | null,
  loading: boolean,
  welcomeSeen: boolean,
  completionSeen: boolean,
): LoginPromptsDecision {
  if (loading) {
    return { showWelcome: false, showCompletion: false, missing: [] };
  }

  if (profile === null) {
    return {
      showWelcome: !welcomeSeen,
      showCompletion: false,
      missing: [],
    };
  }

  const completeness = isProfileComplete(profile);
  if (!completeness.complete && !completionSeen) {
    return {
      showWelcome: false,
      showCompletion: true,
      missing: completeness.missing,
    };
  }

  return { showWelcome: false, showCompletion: false, missing: [] };
}

// ---------------------------------------------------------------------------
// Input / output types
// ---------------------------------------------------------------------------

export interface UseLoginPromptsInput {
  profile: Partial<HealthProfile> | null;
  loading: boolean;
  onNavigateToProfile(): void;
}

export interface UseLoginPromptsResult {
  showWelcome: boolean;
  showCompletion: boolean;
  /** Fields missing from the profile (for ProfileCompletionModal list). */
  missing: string[];
  dismissWelcome(): void;
  dismissCompletion(): void;
}

// ---------------------------------------------------------------------------
// useLoginPrompts
// ---------------------------------------------------------------------------

export function useLoginPrompts({
  profile,
  loading,
}: UseLoginPromptsInput): UseLoginPromptsResult {
  const [showWelcome, setShowWelcome] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);

  useEffect(() => {
    const welcomeSeen = sessionStorage.getItem(KEY_WELCOME) !== null;
    const completionSeen = sessionStorage.getItem(KEY_COMPLETION) !== null;

    const decision = computeLoginPromptsState(
      profile,
      loading,
      welcomeSeen,
      completionSeen,
    );

    setShowWelcome(decision.showWelcome);
    setShowCompletion(decision.showCompletion);
    setMissing(decision.missing);
  }, [loading, profile]);

  const dismissWelcome = (): void => {
    sessionStorage.setItem(KEY_WELCOME, "1");
    setShowWelcome(false);
  };

  const dismissCompletion = (): void => {
    sessionStorage.setItem(KEY_COMPLETION, "1");
    setShowCompletion(false);
  };

  return {
    showWelcome,
    showCompletion,
    missing,
    dismissWelcome,
    dismissCompletion,
  };
}
